import webApiClient from './client';

/**
 * Ticket Management API Client
 *
 * Handles ticket operations for event organizers:
 * - Draft management
 * - Published tickets
 * - QR code operations
 * - Sales reporting
 */

export const ticketApi = {
  /**
   * Get all draft tickets for organization
   * GET /api/v2/ticket/organiser/drafts
   */
  getDrafts: async () => {
    const response = await webApiClient.get('/api/v2/ticket/organiser/drafts');

    if (!response.ok || !response.data || !response.data.ticketDrafts) {
      throw new Error(response.data?.message || 'Failed to fetch draft tickets');
    }

    return response.data.ticketDrafts;
  },

  /**
   * Get all published tickets for organization
   * GET /api/v2/ticket/organiser?status=published
   */
  getPublished: async () => {
    const response = await webApiClient.get('/api/v2/ticket/organiser?status=published');

    if (!response.ok || !response.data || !response.data.tickets) {
      throw new Error(response.data?.message || 'Failed to fetch published tickets');
    }

    return response.data.tickets;
  },

  /**
   * Get non-draft tickets (published + cancelled) for organization.
   * timeframe: 'upcoming' (default — future/live events + all cancelled) or 'past'
   * (ended, non-cancelled events, fetched on demand so they stay off the initial load).
   * GET /api/v2/ticket/organiser?status=all&timeframe=<tf> (filtered client-side to exclude drafts)
   */
  getPublishedAndCancelled: async (timeframe = 'upcoming') => {
    const response = await webApiClient.get(`/api/v2/ticket/organiser?status=all&timeframe=${timeframe}`);

    if (!response.ok || !response.data || !response.data.tickets) {
      throw new Error(response.data?.message || 'Failed to fetch tickets');
    }

    return response.data.tickets.filter(t => t.status !== 'draft');
  },

  /**
   * Get ticket sales report
   * GET /api/v2/ticket/organiser/:ticketId/sales
   */
  getSalesReport: async (ticketId) => {
    const response = await webApiClient.get(`/api/v2/ticket/purchase/sales/${ticketId}`);

    if (!response.ok || !response.data) {
      throw new Error(response.data?.message || 'Failed to fetch sales report');
    }

    return response.data;
  },

  /**
   * Regenerate QR code for ticket
   * POST /api/v2/ticket/organiser/:ticketId/regenerate-qr
   */
  regenerateQR: async (ticketId) => {
    const response = await webApiClient.post(`/api/v2/ticket/organiser/${ticketId}/regenerate-qr`);

    if (!response.ok || !response.data) {
      throw new Error(response.data?.message || 'Failed to regenerate QR code');
    }

    return response.data;
  },

  /**
   * Get QR code for ticket
   * GET /api/v2/ticket/organiser/:ticketId/qr-code
   */
  getQRCode: async (ticketId) => {
    const response = await webApiClient.get(`/api/v2/ticket/organiser/${ticketId}/qr-code`);

    if (!response.ok || !response.data?.qrCodeData?.qrCode) {
      throw new Error(response.data?.message || 'Failed to fetch QR code');
    }

    return response.data.qrCodeData;
  },

  /**
   * Get ticket stats for all published events
   * GET /api/v2/ticket/organiser/stats
   */
  getStats: async () => {
    const response = await webApiClient.get('/api/v2/ticket/organiser/stats');

    if (!response.ok || !response.data || !response.data.eventStats) {
      throw new Error(response.data?.message || 'Failed to fetch ticket stats');
    }

    return response.data.eventStats;
  },

  /**
   * Per-ticket-type sold counts for one config, used by the edit form to floor
   * each quantity input at what's already sold.
   * GET /api/v2/ticket/organiser/:ticketingConfigId/sold-metadata
   * @param {string} ticketingConfigId
   * @returns {Promise<Array<{ name: string, sold: number }>>}
   */
  getSoldMetadata: async (ticketingConfigId) => {
    const response = await webApiClient.get(`/api/v2/ticket/organiser/${ticketingConfigId}/sold-metadata`);

    if (!response.ok || !response.data || !response.data.ticketTypes) {
      throw new Error(response.data?.message || 'Failed to load sold counts');
    }

    return response.data.ticketTypes;
  },

  /**
   * Preview event cancellation — returns attendee list + refund amounts
   * GET /api/v2/ticket/organiser/:ticketingConfigId/cancel-preview
   */
  cancelEventPreview: async (ticketingConfigId) => {
    const response = await webApiClient.get(`/api/v2/ticket/organiser/${ticketingConfigId}/cancel-preview`);

    if (!response.ok || !response.data) {
      throw new Error(response.data?.message || 'Failed to load cancellation preview');
    }

    return response.data;
  },

  /**
   * Get resolved attendee list for printing door list
   * GET /api/v2/ticket/organiser/:ticketId/attendee-list
   */
  getAttendeeList: async (ticketId) => {
    const response = await webApiClient.get(`/api/v2/ticket/organiser/${ticketId}/attendee-list`);
    if (!response.ok || !response.data) {
      throw new Error(response.data?.message || 'Failed to fetch attendee list');
    }
    return response.data; // { attendees, eventDetails, totalTickets }
  },

  /**
   * Confirm event cancellation — process refunds and cancel tickets
   * POST /api/v2/ticket/organiser/:ticketingConfigId/cancel
   */
  cancelEvent: async (ticketingConfigId, reason, refunds) => {
    const response = await webApiClient.post(`/api/v2/ticket/organiser/${ticketingConfigId}/cancel`, {
      reason,
      refunds,
    });

    if (!response.ok || !response.data) {
      throw new Error(response.data?.message || 'Failed to cancel event');
    }

    return response.data;
  },

  /**
   * Validate a ticket purchase session token before proceeding to checkout
   * Returns raw response — callers must inspect response.status for 404/409/410.
   * @param {string} sessionId - Session token to validate
   * @returns {Promise<object>} Raw response — check response.ok and response.status
   */
  validateSession: async sessionId => {
    return webApiClient.post('/api/v2/ticket/sessions/validate', { sessionId });
  },

  /**
   * Get event details for ticket purchase (public/unauthenticated)
   * @param {string} eventId - Event ID
   * @returns {Promise<object>} Response data — callers check .success, read .availableTickets
   * @throws {Error} If fetch fails
   */
  getEventForPurchase: async eventId => {
    const response = await webApiClient.get(`/api/v2/ticket/purchase/event/${eventId}`);

    if (!response.ok || !response.data) {
      const error = new Error(response.data?.message || 'Failed to fetch event for purchase');
      error.status = response.status;
      error.errorCode = response.data?.code || response.data?.errorCode;
      throw error;
    }

    return response.data;
  },

  /**
   * Purchase a ticket for an event
   * @param {object} payload - Purchase details (eventId, ticketTypeId, quantity, etc.)
   * @returns {Promise<object>} Response data from the purchase
   * @throws {Error} If purchase fails
   */
  purchaseTicket: async payload => {
    const response = await webApiClient.post('/api/v2/ticket/purchase', payload);

    if (!response.ok || !response.data) {
      throw new Error(response.data?.message || 'Failed to purchase ticket');
    }

    return response.data;
  },

  /**
   * Get a ticket draft by ID
   * @param {string} draftId - Draft ticket ID
   * @returns {Promise<object>} Ticket draft object
   * @throws {Error} If fetch fails
   */
  getDraft: async draftId => {
    const response = await webApiClient.get(`/api/v2/ticket/organiser/draft/${draftId}`);

    if (!response.ok || !response.data || !response.data.ticketDraft) {
      throw new Error(response.data?.message || 'Failed to fetch ticket draft');
    }

    return response.data.ticketDraft;
  },

  /**
   * Save (create or update) a ticket draft
   * Returns raw response — callers must inspect requiresOwnershipAck, VALIDATION_ERROR code,
   * and ticketDraft.id directly.
   * @param {object} payload - Draft ticket data
   * @returns {Promise<object>} Raw response — check response.ok, response.data
   */
  saveDraft: async payload => {
    return webApiClient.post('/api/v2/ticket/organiser/draft', payload);
  },

  /**
   * Publish a ticket draft for an event
   * @param {string} eventId - Event ID to publish ticket for
   * @returns {Promise<object>} Response data from the publish operation
   * @throws {Error} If publish fails
   */
  publishTicket: async eventId => {
    const response = await webApiClient.post('/api/v2/ticket/organiser/publish', { eventId });

    if (!response.ok || !response.data) {
      throw new Error(response.data?.message || 'Failed to publish ticket');
    }

    return response.data;
  },

  /**
   * Discard the parked edit on a published event — drops the in-progress changes and leaves
   * the live published version untouched. No-op-safe when there is nothing parked.
   * @param {string} eventId - Ticketing config ID
   * @returns {Promise<object>} { id, status, hasUnpublishedChanges: false }
   * @throws {Error} If the discard fails
   */
  discardEdit: async eventId => {
    const response = await webApiClient.post('/api/v2/ticket/organiser/draft/discard-edit', { eventId });

    if (!response.ok || !response.data) {
      throw new Error(response.data?.message || 'Failed to discard changes');
    }

    return response.data.ticketConfig;
  },

  /**
   * Get all tickets for the authenticated attendee
   * @returns {Promise<Array>} Array of ticket objects
   * @throws {Error} If fetch fails
   */
  getMyTickets: async () => {
    const response = await webApiClient.get('/api/v2/ticket/attendee/my-tickets');

    if (!response.ok || !response.data || !response.data.tickets) {
      throw new Error(response.data?.message || 'Failed to fetch my tickets');
    }

    return response.data.tickets;
  },
};
